import { useMemo, useRef, useState } from 'react';

const aliasLabel = { image: '图片', audio: '音频', video: '视频' };

// 把参考素材按"实际发送顺序"重排成连续编号（图片1、图片2、图片3…），无论有几张、无论中途断开过几张。
// 根因：Seedance 按位置解释提示词里的「图片N」——发送顺序里的第 N 张就叫图片N。旧算法保留断开
// 素材的空号，会让第一张图叫成图片2（本次修复的 bug）。生效（已连接）素材优先占 1..K，其余顺延。
export function reconcileReferenceBindings(existing = [], references = []) {
  const normalized = (Array.isArray(existing) ? existing : []).map(binding => ({ ...binding }));
  const connectedIds = new Set(references.map(item => item.nodeId));
  normalized.forEach(binding => { binding.active = connectedIds.has(binding.node_id); });
  // references 已按节点顺序（=发送顺序）排好；缺绑定的补建，别名稍后统一重排。
  references.forEach(item => {
    const current = normalized.find(binding => binding.node_id === item.nodeId);
    if (current) {
      current.active = true;
      current.media_type = item.mediaType;
      return;
    }
    normalized.push({
      id: `ref_${item.mediaType}_${item.nodeId}`,
      node_id: item.nodeId,
      alias: '',
      media_type: item.mediaType,
      purpose: null,
      active: true,
    });
  });
  // 连续重排：先按 references 顺序给生效素材编号，再给失效/断开素材顺延，避免撞号。
  const counters = {};
  const number = binding => {
    const label = aliasLabel[binding.media_type] || '素材';
    counters[binding.media_type] = (counters[binding.media_type] || 0) + 1;
    binding.alias = `${label}${counters[binding.media_type]}`;
  };
  const byNode = new Map(normalized.map(binding => [binding.node_id, binding]));
  references.forEach(item => { const b = byNode.get(item.nodeId); if (b) number(b); });
  normalized.forEach(binding => { if (!references.some(item => item.nodeId === binding.node_id)) number(binding); });
  return normalized;
}

// 重排编号后，把提示词原文里旧的 @图片N 同步改成新号（按 node_id 对齐旧/新别名），
// 否则用户之前 @ 的引用会因为别名变了而匹配不上、显示"已失效"。
export function remapPromptText(text, oldBindings = [], newBindings = []) {
  const source = String(text || '');
  if (!source.includes('@')) return source;
  const newByNode = new Map((newBindings || []).map(binding => [binding.node_id, binding.alias]));
  const pairs = (oldBindings || [])
    .map(binding => ({ from: binding.alias, to: newByNode.get(binding.node_id) }))
    .filter(pair => pair.from && pair.to && pair.from !== pair.to);
  if (!pairs.length) return source;
  // 用占位符两阶段替换，避免 图片1→图片2、图片2→图片3 这类链式误替换。
  let out = source;
  pairs.forEach((pair, index) => {
    out = out.replace(new RegExp(`@${pair.from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![0-9])`, 'g'), `@ ${index} `);
  });
  pairs.forEach((pair, index) => {
    out = out.replace(new RegExp(`@ ${index} `, 'g'), `@${pair.to}`);
  });
  return out;
}

export function promptDocFromText(text, bindings = []) {
  const source = String(text || '');
  const aliases = bindings.filter(binding => binding.alias).sort((a, b) => b.alias.length - a.alias.length);
  if (!aliases.length || !source.includes('@')) return source ? [{ type: 'text', text: source }] : [];
  const escaped = aliases.map(binding => binding.alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`@(${escaped.join('|')})`, 'g');
  const byAlias = new Map(aliases.map(binding => [binding.alias, binding]));
  const doc = [];
  let cursor = 0;
  let match;
  while ((match = pattern.exec(source))) {
    if (match.index > cursor) doc.push({ type: 'text', text: source.slice(cursor, match.index) });
    const binding = byAlias.get(match[1]);
    doc.push({
      type: 'mention',
      binding_id: binding.id,
      node_id: binding.node_id,
      alias: binding.alias,
      media_type: binding.media_type,
      ...(binding.purpose ? { purpose: binding.purpose } : {}),
    });
    cursor = match.index + match[0].length;
  }
  if (cursor < source.length) doc.push({ type: 'text', text: source.slice(cursor) });
  return doc;
}

export default function NodePromptEditor({
  value,
  onChange,
  bindings = [],
  referenceItems = [],
  onPersist,
  placeholder,
  rows = 3,
}) {
  const inputRef = useRef(null);
  const [mention, setMention] = useState(null);
  const itemsByNode = useMemo(() => new Map(referenceItems.map(item => [item.nodeId, item])), [referenceItems]);
  const candidates = useMemo(() => bindings
    .filter(binding => binding.active !== false && itemsByNode.has(binding.node_id))
    .filter(binding => !mention?.query || binding.alias.toLowerCase().includes(mention.query.toLowerCase())), [bindings, itemsByNode, mention]);
  const mentions = useMemo(() => (promptDocFromText(value, bindings)
    .filter(segment => segment.type === 'mention')
    .map(segment => {
      const binding = bindings.find(item => item.id === segment.binding_id);
      return { ...segment, invalid: !binding || binding.active === false || !itemsByNode.has(binding.node_id) };
    })), [value, bindings, itemsByNode]);

  const updateMention = (text, cursor) => {
    const match = /@([^\s@，。！？；：]*)$/.exec(text.slice(0, cursor));
    if (!match) return setMention(null);
    setMention({ start: cursor - match[0].length, end: cursor, query: match[1] || '' });
  };
  const handleChange = event => {
    onChange(event.target.value);
    updateMention(event.target.value, event.target.selectionStart || event.target.value.length);
  };
  const insert = binding => {
    const cursor = inputRef.current?.selectionStart ?? value.length;
    const activeMention = mention || { start: cursor, end: cursor };
    const next = `${value.slice(0, activeMention.start)}@${binding.alias} ${value.slice(activeMention.end)}`;
    const nextCursor = activeMention.start + binding.alias.length + 2;
    onChange(next);
    setMention(null);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };
  const persist = () => onPersist?.({
    promptText: value,
    promptDoc: promptDocFromText(value, bindings),
    referenceBindings: bindings,
  });

  return (
    <div className="relative space-y-1.5">
      <textarea ref={inputRef} value={value} onChange={handleChange}
        onClick={event => updateMention(event.currentTarget.value, event.currentTarget.selectionStart || 0)}
        onKeyUp={event => updateMention(event.currentTarget.value, event.currentTarget.selectionStart || 0)}
        onBlur={() => window.setTimeout(persist, 80)}
        placeholder={placeholder} rows={rows}
        className="w-full resize-none rounded-xl border border-[#ded6c8] bg-[#f7f3eb] px-3 py-2.5 text-[13px] text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-[#8c7db0] dark:border-white/[0.08] dark:bg-black/25 dark:text-gray-200 dark:placeholder-gray-700" />
      {mention && candidates.length > 0 && (
        <div className="absolute left-1 right-1 top-full z-40 mt-1 max-h-40 overflow-y-auto rounded-lg border border-[#ded6c8] bg-white p-1 shadow-xl dark:border-white/[0.1] dark:bg-[#1b1b22]">
          {candidates.map(binding => (
            <button key={binding.id} type="button" onMouseDown={event => { event.preventDefault(); insert(binding); }}
              className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[11px] text-gray-700 hover:bg-[#f7f3eb] dark:text-gray-200 dark:hover:bg-white/[0.06]">
              <span>@{binding.alias}</span>
              <span className="text-[10px] text-gray-400">{aliasLabel[binding.media_type]}</span>
            </button>
          ))}
        </div>
      )}
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {mentions.map((mentionItem, index) => (
            <span key={`${mentionItem.binding_id}-${index}`}
              className={`rounded-md px-1.5 py-0.5 text-[10px] ${mentionItem.invalid
                ? 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300'
                : 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200'}`}>
              @{mentionItem.alias}{mentionItem.invalid ? ' · 已失效' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
